import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wine, Plane, Users, Trophy, GlassWater, GraduationCap, Store, ArrowRight, ChevronDown, Briefcase, Gift, Star } from 'lucide-react';
import SectionTitle from '../components/SectionTitle';
import BigLogo from '../components/BigLogo';
import { useSettings } from '../lib/SettingsContext';
import { useLang, useLocalizedHref } from '../lib/i18n';
import { getExperiences, getTeam, getCollaborators, getOfferings, getNews, resolveImg } from '../lib/api';

const ICONS = { Wine, Plane, Users, Trophy, GlassWater, GraduationCap, Store, Briefcase, Gift, Star };

const formatDate = (iso) => {
  const d = new Date(iso);
  return { day: d.getDate().toString().padStart(2, '0'), month: d.toLocaleString('es-ES', { month: 'short' }).replace('.', '').toUpperCase() };
};

export default function Home() {
  const { settings } = useSettings();
  const { ui } = useLang();
  const href = useLocalizedHref();
  const [experiences, setExperiences] = useState([]);
  const [team, setTeam] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [news, setNews] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    getExperiences('open').then(setExperiences).catch(() => {});
    getTeam().then(setTeam).catch(() => {});
    getCollaborators().then(setCollaborators).catch(() => {});
    getOfferings().then(setOfferings).catch(() => {});
    getNews(3).then(setNews).catch(() => {});
  }, []);

  // Hero carousel auto-rotate every 7 seconds
  useEffect(() => {
    const imgs = settings.hero_images || [];
    if (imgs.length <= 1) return;
    const id = setInterval(() => {
      setHeroIdx((i) => (i + 1) % imgs.length);
    }, 7000);
    return () => clearInterval(id);
  }, [settings.hero_images]);

  useEffect(() => {
    const els = document.querySelectorAll('.gd-fade-in');
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [experiences, team, offerings, collaborators]);

  const upcoming = experiences.slice(0, 4);
  const heroImages = (settings.hero_images && settings.hero_images.length > 0) ? settings.hero_images : [''];
  const ctaImg = resolveImg(settings.hero_images?.[2] || settings.hero_images?.[0] || '');
  const bodegas = collaborators.filter((c) => c.category === 'bodega');
  const sponsors = collaborators.filter((c) => c.category === 'sponsor');

  return (
    <div>
      {/* HERO with carousel */}
      <section className="relative h-[92vh] min-h-[680px] flex items-center justify-center overflow-hidden">
        {heroImages.map((img, i) => (
          <div
            key={i}
            className="absolute inset-0 gd-hero-bg transition-opacity duration-[1500ms] ease-in-out"
            style={{
              backgroundImage: `url(${resolveImg(img)})`,
              opacity: i === heroIdx ? 1 : 0,
              zIndex: i === heroIdx ? 1 : 0,
            }}
          />
        ))}
        <div className="absolute inset-0 z-[2]" style={{ background: 'linear-gradient(180deg, rgba(13,13,14,0.45) 0%, rgba(13,13,14,0.55) 50%, rgba(13,13,14,0.95) 100%)' }} />
        <div className="relative z-10 text-center px-6 max-w-6xl flex flex-col items-center">
          {/* Handwritten tagline at top */}
          <div className="font-script text-white text-3xl md:text-5xl lg:text-6xl leading-tight mb-10 md:mb-14 max-w-4xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
            {settings.hero_title_line1} {settings.hero_title_line2}
          </div>
          {/* Big official logo */}
          <BigLogo />
          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link to={href('/experiencias')} className="gd-btn-gold px-8 py-3.5 text-[12px] uppercase tracking-[0.3em] font-sans-ui font-medium inline-flex items-center justify-center gap-2">{ui('btn.calendar_2026')} <ArrowRight size={14} /></Link>
            <Link to={href('/que-ofrecemos')} className="gd-btn-ghost px-8 py-3.5 text-[12px] uppercase tracking-[0.3em] font-sans-ui font-medium">{ui('btn.what_we_offer')}</Link>
          </div>
          {/* Carousel dots */}
          {heroImages.length > 1 && (
            <div className="flex items-center gap-2 mt-10">
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Imagen ${i + 1}`}
                  onClick={() => setHeroIdx(i)}
                  className={`h-[3px] transition-all duration-500 ${i === heroIdx ? 'w-10 bg-[#c9a063]' : 'w-5 bg-white/35 hover:bg-white/60'}`}
                />
              ))}
            </div>
          )}
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/60 animate-bounce"><ChevronDown size={28} /></div>
      </section>

      {/* CLUB SOCIAL */}
      <section className="py-24 px-6 md:px-10 bg-[#0d0d0e]">
        <div className="max-w-5xl mx-auto text-center gd-fade-in">
          <div className="gd-divider mb-4"><span className="gd-eyebrow">{ui('home.club_eyebrow')}</span></div>
          <div className="font-script text-[#c9a063] text-4xl md:text-5xl mb-3">{ui('home.welcome')}</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-white leading-tight mb-6">{settings.about_title}</h2>
          <p className="text-white/70 text-lg md:text-xl leading-relaxed">{settings.about_text}</p>
        </div>
      </section>

      {/* QUE OFRECEMOS */}
      <section className="py-24 px-6 md:px-10 bg-[#0a0a0b] border-y border-white/5">
        <SectionTitle eyebrow={ui('home.offer_eyebrow')} script={ui('home.offer_script')} title={ui('home.offer_title')} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto mt-14">
          {offerings.map((o) => {
            const Icon = ICONS[o.icon] || Wine;
            const hasUrl = !!(o.url && o.url.trim());
            return (
              <div key={o.id} className="bg-white text-zinc-900 p-8 relative gd-fade-in shadow-lg hover:shadow-xl transition-shadow flex flex-col">
                {o.badge && <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest px-2 py-1 border border-[#a07c44] text-[#a07c44] font-sans-ui bg-white">{o.badge}</span>}
                <div className="w-12 h-12 border border-[#a07c44] flex items-center justify-center mb-5 text-[#a07c44]"><Icon size={20} /></div>
                <h3 className="font-serif-display text-2xl text-zinc-900 mb-3">{o.title}</h3>
                <p className="text-zinc-700 leading-relaxed text-[15.5px] flex-1">{o.description}</p>
                {hasUrl && (
                  <a href={o.url} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center justify-center gap-2 self-start bg-[#a07c44] text-white hover:bg-[#8a6a39] px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] font-sans-ui font-medium transition-colors">
                    {o.cta_label || 'Más información'} <ArrowRight size={13} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* CALENDARIO */}
      <section className="py-24 px-6 md:px-10 bg-[#0d0d0e]">
        <SectionTitle eyebrow={ui('home.cal_eyebrow')} script={ui('home.cal_script')} title={ui('home.cal_title')} subtitle={ui('home.cal_sub')} />
        <div className="max-w-6xl mx-auto mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {upcoming.map((exp) => {
            const { day, month } = formatDate(exp.date);
            return (
              <Link to={`/experiencias/${exp.id}`} key={exp.id} className="gd-card group flex overflow-hidden gd-fade-in">
                <div className="relative w-2/5 min-h-[180px] overflow-hidden">
                  <img src={resolveImg(exp.cover)} alt={exp.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute top-3 left-3 bg-[#0d0d0e]/85 backdrop-blur px-3 py-2 text-center border border-[#c9a063]/40">
                    <div className="font-serif-display text-2xl text-[#c9a063] leading-none">{day}</div>
                    <div className="text-[10px] tracking-widest text-white/70 mt-1">{month}</div>
                  </div>
                </div>
                <div className="flex-1 p-6 flex flex-col justify-center">
                  <div className="text-[10px] tracking-[0.3em] text-[#c9a063] uppercase mb-2">{exp.region}</div>
                  <h3 className="font-serif-display text-xl text-white leading-tight mb-1.5">{exp.title}</h3>
                  {exp.subtitle && <p className="text-white/55 text-sm italic">{exp.subtitle}</p>}
                  <span className="mt-3 text-[11px] uppercase tracking-[0.25em] text-[#c9a063] inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">{ui('btn.view_details')} <ArrowRight size={12} /></span>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="text-center mt-12"><Link to={href('/experiencias')} className="gd-btn-ghost inline-block px-8 py-3 text-[12px] uppercase tracking-[0.3em] font-sans-ui">{ui('btn.view_full_calendar')}</Link></div>
      </section>

      {/* QUIENES SOMOS PREVIEW */}
      <section className="py-24 px-6 md:px-10 bg-[#0a0a0b] border-y border-white/5">
        <SectionTitle eyebrow={ui('home.team_eyebrow')} script={ui('home.team_script')} title={ui('home.team_title')} subtitle={ui('home.team_sub')} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mt-14">
          {team.map((p) => (
            <div key={p.id} className="text-center gd-fade-in">
              <div className="relative overflow-hidden mb-5 aspect-[3/4] bg-[#1a1a1d]">
                {p.photo ? (
                  <img src={resolveImg(p.photo)} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[#c9a063]/40"><Users size={48} /></div>
                )}
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a063] mb-2">{p.role}</div>
              <h3 className="font-serif-display text-2xl text-white mb-3">{p.name}</h3>
              <p className="text-white/60 text-[15px] leading-relaxed">{p.bio}</p>
            </div>
          ))}
        </div>
      </section>

      {/* NOTICIAS TEASER */}
      {news.length > 0 && (
        <section className="py-24 px-6 md:px-10 bg-[#0d0d0e]">
          <SectionTitle eyebrow={ui('home.news_eyebrow')} script={ui('home.news_script')} title={ui('home.news_title')} subtitle={ui('home.news_sub')} />
          <div className="max-w-6xl mx-auto mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {news.map((n) => {
              const d = new Date(n.date);
              return (
                <Link to={`/noticias/${n.slug || n.id}`} key={n.id} className="gd-card group overflow-hidden flex flex-col gd-fade-in">
                  <div className="relative aspect-[16/9] overflow-hidden bg-[#1a1a1d]">
                    {n.image && <img src={resolveImg(n.image)} alt={n.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                  </div>
                  <div className="p-5 flex flex-col gap-2 flex-1">
                    <div className="text-[10px] tracking-[0.3em] text-[#c9a063] uppercase">{d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                    <h3 className="font-serif-display text-lg text-white leading-tight group-hover:text-[#c9a063] transition-colors">{n.title}</h3>
                    <div className="text-white/55 text-sm line-clamp-2 flex-1" dangerouslySetInnerHTML={{ __html: n.content || '' }} />
                    <span className="mt-2 text-[11px] uppercase tracking-[0.25em] text-[#c9a063] inline-flex items-center gap-1.5">{ui('btn.read')} <ArrowRight size={11} /></span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-12"><Link to={href('/noticias')} className="gd-btn-ghost inline-block px-8 py-3 text-[12px] uppercase tracking-[0.3em] font-sans-ui">{ui('btn.view_all_news')}</Link></div>
        </section>
      )}


      {/* COLABORADORES */}
      <section className="py-24 px-6 md:px-10 bg-[#0d0d0e]">
        <SectionTitle eyebrow={ui('home.partners_eyebrow')} title={ui('home.partners_title')} subtitle={ui('home.partners_sub')} />
        <div className="max-w-6xl mx-auto mt-14 space-y-12">
          <div>
            <div className="text-center text-[#c9a063] gd-eyebrow mb-6">{ui('home.wineries')}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {bodegas.map((b) => (
                <div key={b.id} className="gd-card aspect-[3/2] flex flex-col items-center justify-center text-center px-3">
                  {b.logo ? (
                    <img src={resolveImg(b.logo)} alt={b.name} className="max-h-12 max-w-[80%] object-contain mb-2" />
                  ) : null}
                  <div className="font-serif-display text-white text-[15px] leading-tight">{b.name}</div>
                  {b.subtitle && <div className="text-[10px] tracking-widest text-[#c9a063] mt-1.5 uppercase">{b.subtitle}</div>}
                </div>
              ))}
            </div>
          </div>
          {sponsors.length > 0 && (
            <div>
              <div className="text-center text-[#c9a063] gd-eyebrow mb-6">{ui('home.sponsors')}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                {sponsors.map((s) => (
                  <div key={s.id} className="gd-card py-6 px-4 text-center">
                    <div className="font-serif-display text-white text-lg">{s.name}</div>
                    {s.subtitle && <div className="text-[10px] tracking-widest text-[#c9a063] mt-1.5 uppercase">{s.subtitle}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="text-center mt-12"><Link to={href('/colaboradores')} className="gd-btn-ghost inline-block px-8 py-3 text-[12px] uppercase tracking-[0.3em] font-sans-ui">{ui('btn.view_all_partners')}</Link></div>
      </section>

      {/* CTA */}
      <section className="relative py-32 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 gd-hero-bg" style={{ backgroundImage: `url(${ctaImg})` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,13,14,0.85), rgba(13,13,14,0.95))' }} />
        <div className="relative z-10 max-w-3xl mx-auto text-center gd-fade-in">
          <div className="font-script text-[#c9a063] text-4xl md:text-5xl mb-4">¿Nos acompañas?</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-white leading-tight mb-6">Únete a la próxima experiencia</h2>
          <p className="text-white/70 text-lg mb-9">Suscríbete a nuestra newsletter para enterarte de cada torneo, cata y novedad del club.</p>
          <Link to={href('/contacto')} className="gd-btn-gold inline-block px-10 py-3.5 text-[12px] uppercase tracking-[0.3em] font-sans-ui font-medium">Contactar</Link>
        </div>
      </section>
    </div>
  );
}
